import { defineCollection } from "@rebasepro/common";
/**
 * Senders this mailbox has already made a decision about.
 *
 * ── Why a table and not a list in the code ───────────────────────────────────
 * `backend/src/agent/inbox/kind.ts` can tell a robot from a person whenever the
 * message helps it: a `List-Unsubscribe` header, a `noreply@` local part, a
 * calendar's subject line. What it cannot do is recognise the ones that look
 * exactly like correspondence — a tax office writing from `avisocitas@`, a
 * supplier's billing address, one newsletter that sets no list headers at all.
 *
 * Those are learned one at a time, by a person looking at a thread filed wrong
 * and saying so. Without somewhere to keep that decision the same sender is
 * filed wrong again on the next poll, and correcting it is worth nothing — so
 * people stop.
 *
 * ── Both directions, deliberately ────────────────────────────────────────────
 * A rule can say `automated` *or* `person`. The second exists because the
 * heuristics are tuned to err towards "automated" only where that is cheap, and
 * they still get it wrong: somebody whose mail Gmail files under Promotions, or
 * who titles every message "Invitation: ...". A `person` rule outranks every
 * signal, which is the only way a correction can stick against a header that
 * will keep arriving.
 *
 * ── What a pattern matches ───────────────────────────────────────────────────
 * A whole address (`billing@acme.com`), or a bare domain (`acme.com`), which
 * covers its subdomains — silencing a company was not meant to exclude the parts
 * of it that send from a different host. Never a bare TLD.
 */
const emailSenderRulesCollection = defineCollection({
    name: "Sender rules",
    singularName: "Sender rule",
    slug: "email_sender_rules",
    table: "email_sender_rules",
    description: "What to assume about a sender the headers do not give away. Written by correcting a conversation.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        pattern: {
            name: "Address or domain",
            type: "string",
            validation: { required: true, unique: true },
            description: "billing@acme.com, or acme.com for everything from that company and its subdomains"
        },
        verdict: {
            name: "Treat as",
            type: "string",
            validation: { required: true },
            defaultValue: "automated",
            enum: [
                { id: "automated", label: "A machine", color: "gray" },
                { id: "person", label: "A person", color: "green" }
            ]
        },
        note: {
            name: "Why",
            type: "string",
            description: "One line, for whoever reads this list in six months and wonders"
        },
        created_by: { name: "Written by", type: "string", admin: { readOnly: true } },
        created_at: { name: "Created", type: "date", autoValue: "on_create", admin: { readOnly: true } }
    },
    admin: {
        icon: "Rule",
        group: "Outreach",
        defaultViewMode: "table",
        display: { title: "pattern", subtitle: "note" },
        sort: ["created_at", "desc"],
        propertiesOrder: ["pattern", "verdict", "note", "created_by", "created_at"]
    }
});
export default emailSenderRulesCollection;
